import { API, graphqlOperation } from 'aws-amplify';
import { GraphQLResult, RecordOption, many2Many } from '..';

export const graphqlCall = async <T = any, K = any>(
  op: string,
  params?: K
): Promise<GraphQLResult<T>> => {
  try {
    const res = await API.graphql(graphqlOperation(op, params as any));

    if ('data' in res) {
      return res as GraphQLResult<T>;
    } else {
      return {
        data: {} as T,
      };
    }
  } catch (ex: any) {
    console.log('Error in GraphQL Query', ex);
    return ex as GraphQLResult<T>;
  }
};

/** Create a "List All" query for the given amplify graphql query */
export const createListAllQuery =
  <ResponseType = any, ParamType = any>(query: string, attr: string) =>
  async (params?: ParamType): Promise<ResponseType[]> =>
    listAll(query, attr, params);

/** Recursively calls the "List All" query to fetch all items with the given query */
const recursiveGraphqlListCall = async <T = any, K = any>(
  query: string,
  attr: string,
  variables?: K
): Promise<{ nextToken?: string; items: T[] }> => {
  const res = await graphqlCall(query, variables);

  const data = res.data[attr];

  const items = [
    ...(data?.items || []),
    ...(data?.nextToken
      ? await listAll(query, attr, {
          ...variables,
          nextToken: data.nextToken,
        })
      : []),
  ];

  return {
    nextToken: data?.nextToken,
    items,
  };
};

export const listAll = async <T = any, K = any>(
  query: string,
  attr: string,
  variables: K
): Promise<T[]> => {
  const { items } = await recursiveGraphqlListCall(query, attr, variables);
  return items;
};

export const formatMany2Many = <T, U>(
  m2mItems: U[],
  formatter: (i: U) => T
): many2Many<T, U> => ({
  items: m2mItems.map(formatter),
  m2mItems,
});

export const getMany2ManyOperations = <
  T extends { id: string },
  U extends { id: string }
>(
  m2m: many2Many<T, U>
) => ({
  // The options ids to create
  toCreate: m2m.items.reduce(
    (acc: T[], curr) =>
      !m2m.m2mItems.map((i) => i.id).includes(curr.id) ? [...acc, curr] : acc,
    []
  ),
  // The M2m records to delete
  toDelete: m2m.m2mItems.reduce(
    (acc: U[], curr) =>
      !m2m.items.map((i) => i.id).includes(curr.id) ? [...acc, curr] : acc,
    []
  ),
});

export const getMany2manyDiff = (
  currentIds: string[] = [],
  many2ManyItems: RecordOption[] = []
) => ({
  // The options ids to create
  toCreate: currentIds.reduce(
    (acc: string[], curr) =>
      !many2ManyItems.map((i) => i.optionID).includes(curr)
        ? [...acc, curr]
        : acc,
    []
  ),
  // The M2m records to delete
  toDelete: many2ManyItems.reduce(
    (acc: string[], curr) =>
      !currentIds.includes(curr.optionID as string)
        ? [...acc, curr.id as string]
        : acc,
    []
  ),
});

export const omitTypename = <T extends { __typename: string }>(
  item: T | null | undefined
) => {
  if (item === null || item === undefined) {
    return item;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { __typename, ...rest } = item;

  return rest;
};
